import type React from 'react';

export type CardVariant = 'default' | 'elevated' | 'bordered' | 'glass';
export type CardSize = 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  size?: CardSize;
  hover?: boolean;
  children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({
  variant = 'default',
  size = 'md',
  hover = false,
  className = '',
  children,
  ...props
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'p-3 rounded-lg',
    md: 'p-4 rounded-xl',
    lg: 'p-6 rounded-2xl'
  };

  // Variant classes
  const variantClasses = {
    default: [
      'bg-bg-secondary',
      'border',
      'border-border-light',
      'shadow-sm'
    ],
    elevated: [
      'bg-bg-secondary',
      'border',
      'border-border-light',
      'shadow-md'
    ],
    bordered: [
      'bg-bg-secondary',
      'border',
      'border-border-medium'
    ],
    glass: [
      'bg-bg-secondary',
      'border',
      'border-border-light',
      'shadow-sm',
      'backdrop-blur-md'
    ]
  };

  // Hover effects
  const hoverClasses = hover ? [
    'hover:shadow-md',
    'hover:border-border-medium',
    'hover:-translate-y-0.5',
    'transition-all',
    'duration-200'
  ] : [
    'transition-shadow',
    'duration-200'
  ];

  const baseClasses = [
    'relative',
    'overflow-hidden'
  ];

  const classes = [
    ...baseClasses,
    sizeClasses[size],
    ...variantClasses[variant],
    ...hoverClasses,
    className
  ].filter(Boolean).join(' ');

  // Apply glass effect background for glass variant
  const style = variant === 'glass' ? {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
    backdropFilter: 'blur(10px)',
    ...props.style
  } : props.style;

  return (
    <div 
      className={classes} 
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

Card.displayName = 'Card';

export { Card };
export default Card;